import { env } from "@/lib/env/env";
import { getAdminMe, type AdminMeResponse } from "@/lib/api/admin-auth-api";
import { ApiError } from "@/lib/api/api-error";
import type { AdminSession } from "@/lib/auth/admin-session";
import { refreshAdminAccessToken } from "@/lib/auth/refresh-admin-tokens";
import {
  clearPersistedAdminSession,
  getPersistedAccessToken,
  getPersistedRefreshToken,
  setPersistedAccessToken,
} from "@/lib/auth/admin-session-storage";
import {
  getInMemoryAccessToken,
  setInMemoryAccessToken,
} from "@/lib/auth/admin-token-storage";
import type { AdminRole } from "@/lib/permissions/roles";

export function adminMeToSession(me: AdminMeResponse): AdminSession {
  return {
    adminId: me.admin.id,
    email: me.admin.email,
    displayName: me.admin.display_name,
    role: me.admin.role as AdminRole,
    mfaEnabled: me.admin.mfa_enabled,
    signedInAt: new Date().toISOString(),
    environmentLabel: env.VITE_APP_ENV,
    permissions: me.admin.permissions,
  };
}

function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/**
 * Rehydrate an admin session after a full page reload (real mode only).
 * Tries persisted access token first, then refresh token if the backend
 * exposes /admin/auth/refresh.
 */
export async function restoreAdminSession(): Promise<AdminSession | null> {
  const persistedAccess = getPersistedAccessToken();

  if (persistedAccess) {
    setInMemoryAccessToken(persistedAccess);
    try {
      const me = await getAdminMe(persistedAccess);
      return adminMeToSession(me);
    } catch (err) {
      if (!isUnauthorized(err)) throw err;
      setInMemoryAccessToken(null);
      setPersistedAccessToken(null);
    }
  }

  const newAccess = await refreshAdminAccessToken();
  if (newAccess) {
    const me = await getAdminMe(newAccess);
    return adminMeToSession(me);
  }

  setInMemoryAccessToken(null);
  clearPersistedAdminSession();
  return null;
}

/** Whether persisted credentials exist (used to skip restore in mock mode). */
export function hasPersistedAdminSession(): boolean {
  return Boolean(getPersistedAccessToken() || getPersistedRefreshToken());
}

/** In-memory token after restore attempt (for tests). */
export function getRestoredAccessToken(): string | null {
  return getInMemoryAccessToken();
}
